import { type ReactNode, useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, isTouchDevice, Link, TextField } from "@shapr3d/frontend-lib";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z, type ZodType } from "zod";

import {
  accountDeletionSupportArticleUrl,
  appStoreSubscriptionsUrl,
  exportSupportArticleUrl,
  queryKeys,
} from "../../config";
import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { useLogout } from "../../hooks/useAuth";
import useTranslation from "../../hooks/useTranslation";
import { UMSrequestClient } from "../../stores/requestClient";

import style from "./DeleteAccountForm.module.css";

const schema = z.object({
  email: z.string().nonempty(),
});

const useDeleteAccount = () => {
  const { mutateAsync: mutateLogout } = useLogout({
    redirect: false,
    withRequest: false,
  });
  const navigation = useNavigation();

  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "delete-account"],
    mutationFn: () =>
      UMSrequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/account`,
      }),
    onSuccess: async () => {
      try {
        await mutateLogout();
      } finally {
        navigation.goToAccountDeletionSuccess();
      }
    },
  });
};

type DeleteAccountData = z.infer<typeof schema>;

export default function DeleteAccountForm({
  onCancel,
  open,
}: {
  onCancel?: () => void;
  open?: boolean;
}) {
  const [refinedSchema, setRefinedSchema] = useState<ZodType>(schema);
  const { t, m } = useTranslation();
  const { data: accountData } = useAccount();

  useEffect(() => {
    setRefinedSchema(
      schema.refine((data) => data.email === accountData?.email, {
        message: t(m.Account.DeleteAccount.EmailDoesNotMatch),
      })
    );
  }, [accountData?.email, t, m.Account.DeleteAccount.EmailDoesNotMatch]);

  const {
    register,
    handleSubmit,
    formState: { isValid, isSubmitting },
    reset,
  } = useForm<DeleteAccountData>({
    resolver: zodResolver(refinedSchema),
    defaultValues: {
      email: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (open) {
      reset({ email: "" });
    }
  }, [open, reset]);

  const { mutateAsync: mutateDeleteAccount, isError: isDeleteAccountError } = useDeleteAccount();

  const submit = async () => {
    try {
      await mutateDeleteAccount();
    } catch (error) {
      // This is to not throw Unhandled Promise Rejection error
    }
  };

  return (
    <form className={style.deleteAccountForm} onSubmit={handleSubmit(submit)}>
      {Boolean(accountData?.subscriptions?.length) && (
        <p>
          {t(
            accountData?.hasAppStoreSubscription
              ? m.Account.DeleteAccount.BeforeDeletingExportAndCancel
              : m.Account.DeleteAccount.BeforeDeletingExportOnly,
            {
              exportLink: (chunks: ReactNode) => (
                <Link href={exportSupportArticleUrl} rel="noreferrer noopener" target="_blank">
                  {chunks}
                </Link>
              ),
              cancelLink: (chunks: ReactNode) => (
                <Link href={appStoreSubscriptionsUrl} rel="noreferrer noopener" target="_blank">
                  {chunks}
                </Link>
              ),
            }
          )}
        </p>
      )}
      <p>{t(m.Account.DeleteAccount.PermanentlyDeleteModal)}</p>
      <p className={style.footNote}>
        {t(m.Account.DeleteAccount.LearnMore, {
          link: (chunks: ReactNode) => (
            <Link href={accountDeletionSupportArticleUrl} rel="noreferrer noopener" target="_blank">
              {chunks}
            </Link>
          ),
        })}
      </p>
      <TextField
        placeholder={accountData?.email}
        autoComplete="off"
        autoFocus={!isTouchDevice()}
        {...register("email")}
      ></TextField>
      <div className={style.actionButtons}>
        {onCancel && (
          <Button variant="grayscale" onClick={onCancel}>
            {t(m.Account.DeleteAccount.Cancel)}
          </Button>
        )}
        <Button type="submit" variant="red" disabled={!isValid || isSubmitting}>
          {t(m.Account.DeleteAccount.DeleteButton)}
        </Button>
      </div>
      {isDeleteAccountError && <div>{t(m.ErrorMessage.Unknown)}</div>}
    </form>
  );
}
